import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "../store";

// Use direct backend URL in development (bypasses Vite proxy)
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

// Define PromptData interface locally since WorkProductWorkshop has been deleted
interface PromptData {
    [key: string]: any;
}

interface WorkProductProcessorState {
    status: "idle" | "loading" | "succeeded" | "failed";
    buildInProgress: boolean;
    error?: string | null;
    completed?: boolean;
    chunkReceived?: string | null;
    chunkCounter: number;
}

const initialState: WorkProductProcessorState = {
    status: "idle",
    buildInProgress: false,
    error: null,
    completed: false,
    chunkReceived: null,
    chunkCounter: 0
};

const workProductProcessorSlice = createSlice({
    name: "adminWorkProductProcessor",
    initialState,
    reducers: {
        buildWorkProductStarted(state) {
            state.buildInProgress = true;
            state.chunkReceived = "";
            state.error = null;
            state.completed = false;
            state.chunkCounter = 0;
        },
        buildWorkProductChunkReceived(state, action: PayloadAction<string>) {
            state.chunkReceived = action.payload;
            state.chunkCounter++;
            window.dispatchEvent(new CustomEvent("chunkReceivedEvent", { detail: action.payload }));
        },
        buildWorkProductSucceeded(state) {
            console.log("JOB FINISHED");
            state.buildInProgress = false;
            state.status = "succeeded";
            state.completed = true;
        },
        buildWorkProductFailed(state, action: PayloadAction<string>) {
            state.buildInProgress = false;
            state.error = action.payload;
            state.status = "failed";
            state.completed = true;
        },
        resetStudioState(state) {
            state.status = "idle";
            state.error = null;
            state.buildInProgress = false;
            state.completed = false;
            state.chunkReceived = null;
            state.chunkCounter = 0; // Reset the chunk counter as well
        }
    }
});

// Export the actions
export const { resetStudioState, buildWorkProductStarted, buildWorkProductChunkReceived, buildWorkProductSucceeded, buildWorkProductFailed } =
    workProductProcessorSlice.actions;

export default workProductProcessorSlice.reducer;

// Thunk for the streaming API call
export const buildWorkProduct =
    (projectid?: number, workproductid?: number, sectionid?: number, promptData?: PromptData, promptBuilder?: Boolean) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000);

        dispatch(resetStudioState());

        try {
            console.log("PROCESS STARTING");

            dispatch(buildWorkProductStarted());

            const state = getState() as RootState;
            const idToken = state.clientToken.token;

            if (!idToken) {
                dispatch(buildWorkProductFailed("No token available"));
                return;
            }

            console.log("CALLING BUILD API");

            const response = await fetch(`${BACKEND_URI}/api/workproduct_utility/buildworkproduct`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    projectid: projectid,
                    workproductid: workproductid,
                    sectionid: sectionid,
                    promptData: promptData,
                    promptBuilder: promptBuilder
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                dispatch(buildWorkProductFailed(`HTTP error! status: ${response.status}`));
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // The streaming part:
            const reader = response.body?.getReader();

            if (!reader) {
                dispatch(buildWorkProductFailed(`ReadableStream not supported in this environment`));
                throw new Error("ReadableStream not supported in this environment");
            }

            const decoder = new TextDecoder();
            let accumulatedData = ""; // Buffer for storing chunks
            let done = false;

            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;

                if (value) {
                    let chunkText = decoder.decode(value, { stream: true }).trim();
                    accumulatedData += chunkText; // Append chunk to buffer

                    const matches = [...accumulatedData.matchAll(/<<<STREAM-START>>>((?:.|\n)*?)<<<STREAM-END>>>/g)];

                    if (matches.length == 0) {
                        // If no matches found, continue to accumulate data
                        console.log("⚠️ No complete STREAM-START/STREAM-END block found yet.");
                        continue;
                    }
                    matches.forEach(match => {
                        // Extract all JSON objects within the STREAM-START and STREAM-END
                        const jsonMatch = match[1];
                        const jsonToSend = JSON.parse(jsonMatch);
                        dispatch(buildWorkProductChunkReceived(jsonToSend));
                    });
                }
            }

            console.log("Finished reading stream");
            dispatch(buildWorkProductSucceeded());
        } catch (error: any) {
            dispatch(buildWorkProductFailed(error.message || "Error building work product"));
        }
    };
